import * as React from "react";
import { useCallback, useMemo, useState } from "react";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment/moment";
import { useTranslation } from "react-i18next";
import { Row, Col, Input, FormGroup, HelpBlock, ControlLabel, Alert } from "components/bootstrap";
import { Select, Spinner, TimeUnitInput, SearchFiltersFormControls, TimezoneSelect } from "components/common";
import { extractDurationAndUnit } from "components/common/TimeUnitInput";
import { TIME_UNITS } from "components/event-definitions/event-definition-types/FilterForm";
import useUserDateTime from "hooks/useUserDateTime";
import * as FormsUtils from "util/FormsUtils";
import { describeExpression } from "util/CronUtils";
import { useGetStreams } from "security-app/hooks/useSigmaAPI";
import StreamsAndCategoriesFilter from "views/components/common/StreamsAndCategoriesFilter";
import useLicenseCheck from "license/useLicenseCheck";
import { ANOMALY_SUBTYPES, getSubtypeByType } from "./anomalySubtypes";
const AnomalyEventForm = ({ eventDefinition, onChange, validation }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const { t } = useTranslation("enterprise_security_events");
  const [currentConfig, setCurrentConfig] = useState(() => ({
    ...eventDefinition.config
  }));
  const selectedSubtype = ((_a = currentConfig.config) == null ? void 0 : _a.type) ? getSubtypeByType(currentConfig.config.type) : void 0;
  const {
    execute_every_ms: executeEveryMs,
    cron_expression: cronExpression,
    window_delay_ms: windowDelayMs
  } = currentConfig;
  const executeEvery = extractDurationAndUnit(executeEveryMs, TIME_UNITS);
  const [executeEveryMsDuration, setExecuteEveryMsDuration] = useState(executeEvery.duration);
  const [executeEveryMsUnit, setExecuteEveryMsUnit] = useState(executeEvery.unit);
  const windowDelay = extractDurationAndUnit(windowDelayMs, TIME_UNITS);
  const [windowDelayMsDuration, setWindowDelayMsDuration] = useState(windowDelay.duration);
  const [windowDelayMsUnit, setWindowDelayMsUnit] = useState(windowDelay.unit);
  const { userTimezone } = useUserDateTime();
  const [cronDescription, setCronDescription] = useState(
    cronExpression ? describeExpression(cronExpression) : ""
  );
  const { streams, loadingStreams } = useGetStreams();
  const licenseCheck = useLicenseCheck();
  const isIlluminate = eventDefinition._scope === "ILLUMINATE";
  const subtypeOptions = useMemo(() => {
    const availableSubtypes = ANOMALY_SUBTYPES.filter((subtype) => {
      if (!subtype.condition) return true;
      return subtype.condition(licenseCheck, eventDefinition);
    });
    return availableSubtypes.map((subtype) => ({
      value: subtype.type,
      label: subtype.displayName
    }));
  }, [eventDefinition, licenseCheck]);
  const setCurrentConfigAndPropagateChange = useCallback(
    (config) => {
      setCurrentConfig(config);
      onChange("config", config);
    },
    [onChange]
  );
  const handleSubtypeChange = (nextValue) => {
    var _a2;
    if (!nextValue) return;
    const subtype = getSubtypeByType(nextValue);
    if (!subtype) return;
    const config = cloneDeep(eventDefinition.config);
    config.config = subtype.defaultConfig;
    if (subtype.parentConfigOverrides) {
      Object.assign(config, subtype.parentConfigOverrides);
    }
    if (((_a2 = subtype.parentConfigOverrides) == null ? void 0 : _a2.execute_every_ms) !== void 0) {
      const newEvery = extractDurationAndUnit(subtype.parentConfigOverrides.execute_every_ms, TIME_UNITS);
      setExecuteEveryMsDuration(newEvery.duration);
      setExecuteEveryMsUnit(newEvery.unit);
    }
    setCurrentConfigAndPropagateChange(config);
  };
  const handleConfigChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const config = cloneDeep(eventDefinition.config);
    config[name] = value;
    setCurrentConfigAndPropagateChange(config);
  };
  const handleStreamsAndCategoriesChange = useCallback(
    (selected) => {
      const config = cloneDeep(eventDefinition.config);
      config.stream_categories = selected.categories;
      config.streams = selected.streams;
      setCurrentConfigAndPropagateChange(config);
    },
    [eventDefinition.config, setCurrentConfigAndPropagateChange]
  );
  const handleSearchFiltersChange = useCallback(
    (searchFilters) => {
      const config = cloneDeep(eventDefinition.config);
      config.filters = searchFilters.toArray();
      setCurrentConfigAndPropagateChange(config);
    },
    [eventDefinition.config, setCurrentConfigAndPropagateChange]
  );
  const handleExecuteEveryChange = (nextValue, nextUnit) => {
    const durationInMs = moment.duration(Math.max(nextValue, 1), nextUnit).asMilliseconds();
    const config = cloneDeep(eventDefinition.config);
    config.execute_every_ms = durationInMs;
    setExecuteEveryMsDuration(nextValue);
    setExecuteEveryMsUnit(nextUnit);
    setCurrentConfigAndPropagateChange(config);
  };
  const handleWindowDelayChange = (nextValue, nextUnit) => {
    const durationInMs = moment.duration(Math.max(nextValue, 0), nextUnit).asMilliseconds();
    const config = cloneDeep(eventDefinition.config);
    config.window_delay_ms = durationInMs;
    setWindowDelayMsDuration(nextValue);
    setWindowDelayMsUnit(nextUnit);
    setCurrentConfigAndPropagateChange(config);
  };
  const handleCronTimezoneChange = (tz) => {
    const config = cloneDeep(eventDefinition.config);
    config.cron_timezone = tz;
    setCurrentConfigAndPropagateChange(config);
  };
  const handleUseCronSchedulingChange = (event) => {
    const { name } = event.target;
    const value = FormsUtils.getValueFromInput(event.target);
    const config = cloneDeep(eventDefinition.config);
    config[name] = value;
    if (value) {
      config.cron_expression = "";
      config.cron_timezone = userTimezone;
    } else {
      config.cron_expression = null;
      config.cron_timezone = null;
    }
    setCurrentConfigAndPropagateChange(config);
  };
  const SubtypeFormComponent = selectedSubtype == null ? void 0 : selectedSubtype.formComponent;
  const currentSelection = {
    streams: (_b = currentConfig.streams) != null ? _b : [],
    categories: (_c = currentConfig.stream_categories) != null ? _c : []
  };
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 7, lg: 6 }, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "anomaly-event-subtype" }, /* @__PURE__ */ React.createElement(ControlLabel, null, t("anomaly_event_form.detection_type")), /* @__PURE__ */ React.createElement(
    Select,
    {
      id: "anomaly-event-subtype-select",
      name: "subtype",
      placeholder: t("anomaly_event_form.select_detection_type"),
      clearable: false,
      menuPlacement: "bottom",
      value: (_d = currentConfig.config) == null ? void 0 : _d.type,
      options: subtypeOptions,
      onChange: handleSubtypeChange,
      disabled: isIlluminate
    }
  ), selectedSubtype && /* @__PURE__ */ React.createElement(HelpBlock, null, selectedSubtype.description)), !selectedSubtype && /* @__PURE__ */ React.createElement(Alert, { bsStyle: "info" }, t("anomaly_event_form.no_detection_method")), SubtypeFormComponent && /* @__PURE__ */ React.createElement(
    SubtypeFormComponent,
    {
      eventDefinition,
      onChange,
      validation,
      section: "query"
    }
  ), selectedSubtype && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "search-filters" }, /* @__PURE__ */ React.createElement(ControlLabel, null, t("anomaly_event_form.search_filters"), " ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, t("common.optional"))), /* @__PURE__ */ React.createElement("div", { style: { margin: "16px 0" } }, /* @__PURE__ */ React.createElement(SearchFiltersFormControls, { filters: currentConfig.filters, onChange: handleSearchFiltersChange }))), selectedSubtype && !isIlluminate && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(FormGroup, { controlId: "filter-streams-and-categories" }, /* @__PURE__ */ React.createElement(ControlLabel, null, t("anomaly_event_form.streams_and_categories")), loadingStreams ? /* @__PURE__ */ React.createElement(Spinner, { text: t("anomaly_event_form.loading_streams") }) : /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    StreamsAndCategoriesFilter,
    {
      id: "filter-streams-and-categories",
      onChange: handleStreamsAndCategoriesChange,
      value: currentSelection,
      streams
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, t("anomaly_event_form.select_streams_help")))), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "anomaly-is-cron-checkbox",
      type: "checkbox",
      name: "use_cron_scheduling",
      label: t("anomaly_event_form.use_cron_scheduling"),
      help: t("anomaly_event_form.cron_scheduling_help"),
      checked: (_e = currentConfig.use_cron_scheduling) != null ? _e : false,
      onChange: handleUseCronSchedulingChange
    }
  ), currentConfig.use_cron_scheduling ? /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(
    FormGroup,
    {
      controlId: "anomaly-cron-expression",
      validationState: validation.errors.cron_expression ? "error" : null
    },
    /* @__PURE__ */ React.createElement(
      Input,
      {
        id: "anomaly-cron-expression",
        name: "cron_expression",
        label: t("anomaly_event_form.cron_expression"),
        type: "text",
        help: /* @__PURE__ */ React.createElement("span", null, cronDescription || t("anomaly_event_form.cron_placeholder")),
        value: (_f = currentConfig.cron_expression) != null ? _f : "",
        onBlur: () => setCronDescription(describeExpression(currentConfig.cron_expression)),
        onChange: handleConfigChange
      }
    ),
    validation.errors.cron_expression && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.cron_expression[0])
  ), /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, t("anomaly_event_form.cron_time_zone")), /* @__PURE__ */ React.createElement(
    TimezoneSelect,
    {
      value: (_g = currentConfig.cron_timezone) != null ? _g : userTimezone,
      name: "cron_timezone",
      clearable: false,
      onChange: handleCronTimezoneChange
    }
  ))) : /* @__PURE__ */ React.createElement(
    FormGroup,
    {
      controlId: "anomaly-execute-every",
      validationState: validation.errors.execute_every_ms ? "error" : null
    },
    /* @__PURE__ */ React.createElement(
      TimeUnitInput,
      {
        label: t("anomaly_event_form.execute_search_every"),
        update: handleExecuteEveryChange,
        value: executeEveryMsDuration,
        unit: executeEveryMsUnit,
        units: TIME_UNITS,
        clearable: true,
        required: true
      }
    ),
    validation.errors.execute_every_ms && /* @__PURE__ */ React.createElement(HelpBlock, null, validation.errors.execute_every_ms[0])
  )), selectedSubtype && /* @__PURE__ */ React.createElement(FormGroup, { controlId: "anomaly-window-delay" }, /* @__PURE__ */ React.createElement(
    TimeUnitInput,
    {
      label: t("anomaly_event_form.window_delay"),
      update: handleWindowDelayChange,
      value: windowDelayMsDuration,
      unit: windowDelayMsUnit,
      units: TIME_UNITS,
      clearable: true,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, t("anomaly_event_form.window_delay_help"))), SubtypeFormComponent && /* @__PURE__ */ React.createElement(
    SubtypeFormComponent,
    {
      eventDefinition,
      onChange,
      validation,
      section: "config"
    }
  ), selectedSubtype && !isIlluminate && /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "anomaly-schedule-checkbox",
      type: "checkbox",
      name: "_is_scheduled",
      label: t("anomaly_event_form.enable"),
      help: t("anomaly_event_form.enable_help"),
      checked: (_h = currentConfig._is_scheduled) != null ? _h : true,
      onChange: handleConfigChange
    }
  )), SubtypeFormComponent && (selectedSubtype == null ? void 0 : selectedSubtype.hasSidebar) && /* @__PURE__ */ React.createElement(Col, { md: 5, lgOffset: 1 }, /* @__PURE__ */ React.createElement(
    SubtypeFormComponent,
    {
      eventDefinition,
      onChange,
      validation,
      section: "sidebar"
    }
  )));
};
AnomalyEventForm.defaultConfig = {
  streams: [],
  filters: [],
  stream_categories: [],
  search_within_ms: 0,
  execute_every_ms: 5 * 60 * 1e3,
  window_delay_ms: 6e4,
  _is_scheduled: true,
  use_cron_scheduling: false,
  cron_expression: null,
  cron_timezone: null,
  config: null
  // No subtype selected initially
};
export default AnomalyEventForm;
